import { useMemo, type ComponentProps } from 'react';
import { randomColor } from 'randomcolor';

import { Avatar, AvatarImage, AvatarFallback } from '@/shadcn/avatar';

type Props = {
  source?: string | null | undefined;
  name?: string | null | undefined;
  colorized?: boolean;
  size?: number;
} & ComponentProps<typeof Avatar>;

export const AvatarComponent = ({
  source,
  name,
  colorized = true,
  className,
  size = 32,
  ...props
}: Props) => {
  const initials = getInitials(name);
  const color = useMemo(() => {
    if (!colorized || !name) return undefined;
    return randomColor({
      seed: name,
      luminosity: 'light',
      format: 'rgba',
      alpha: 0.2
    });
  }, [colorized, name]);

  const fontSize = useMemo(() => {
    if (size <= 0) return 0;
    if (initials?.length && initials.length > 1) {
      return size / 2.5;
    }
    return size / 1.8;
  }, [size, initials]);

  return (
    <Avatar
      className={className}
      {...props}
      style={{ width: size, height: size }}
    >
      <AvatarImage
        src={source ?? undefined}
        alt={name ? `${name}'s avatar` : 'An avatar'}
      />
      <AvatarFallback
        delayMs={source ? undefined : 0}
        style={{ backgroundColor: color }}
      >
        {initials && (
          <span
            className="text-contrast/80 mt-0.5 inline-block font-mono leading-0 tracking-widest uppercase"
            style={{ fontSize: fontSize }}
          >
            {initials}
          </span>
        )}
      </AvatarFallback>
    </Avatar>
  );
};

AvatarComponent.displayName = 'AvatarComponent';

const getInitials = (name?: string | null): string | null => {
  if (!name) return null;
  const words = name.split(' ');
  const filteredWords = words.filter(word => /^[a-zA-Z0-9]+$/.test(word));
  if (filteredWords.length === 0) return null;
  return filteredWords
    .slice(0, 2)
    .map(n => n.charAt(0))
    .join('');
};
